import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { collection, doc, setDoc, deleteDoc, onSnapshot } from "firebase/firestore";
import { tokens } from "./theme";

const { color: C, radius: R, shadow: SH, font: F } = tokens;

// ─────────────────────────────────────────────────────────────────────────────
// EMPTY FORM SHAPES
// ─────────────────────────────────────────────────────────────────────────────
const emptyTeamMember = { role: "Employer", name: "", email: "", organisation: "" };

const emptyDocument = {
  name: "",
  category: "Statutory",
  neverExpires: false,
  expiryDate: "",
  reference: "",
};

const emptySub = {
  name: "",
  type: "Subcontractor",
  scope: "",
  performance: "Satisfactory",
};

const emptyParticipationForm = {
  cpglPercentage: "",
  cpgePercentage: "",
  captureMonth: "",
  latestCpglValue: "",
  latestCpgeValue: "",
};

// Standard pre-commencement documentation (C1.2 Contract Data, Clause 5.3.1)
// plus the recurring good-standing items tracked in the contract meetings.
const SUGGESTED_DOCS = [
  { name: "Approved Health & Safety Plan", category: "Safety" },
  { name: "Occupational Health & Safety Agreement", category: "Safety" },
  { name: "Letter of Good Standing — Compensation Commissioner (COID)", category: "Statutory" },
  { name: "Letter of Good Standing — Bargaining Council (BCCEI)", category: "Statutory" },
  { name: "Evidence of Insurance", category: "Insurance" },
  { name: "Contractors All Risk (CAR) Insurance", category: "Insurance" },
  { name: "Public Liability Insurance", category: "Insurance" },
  { name: "Performance Security / Surety", category: "Surety" },
  { name: "Protection of the Environment Declaration", category: "Environmental" },
  { name: "Initial Programme", category: "Programme" },
  { name: "CIDB Registration", category: "Statutory" },
  { name: "SARS Tax Compliance", category: "Statutory" },
];

const DOC_CATEGORIES = [
  "Statutory",
  "Insurance",
  "Surety",
  "Safety",
  "Environmental",
  "Programme",
  "Other",
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const EXPIRING_SOON_DAYS = 30;

function numberOrZero(value) {
  return Number(value || 0);
}

function clampProgress(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

function daysUntil(expiryDate) {
  if (!expiryDate) return null;
  const expiry = new Date(expiryDate);
  if (Number.isNaN(expiry.getTime())) return null;
  expiry.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((expiry - today) / 86400000);
}

// Returns { label, tone } where tone maps to the status palette.
function expiryStatus(row) {
  if (row.neverExpires) return { label: "No expiry", tone: "neutral" };
  if (!row.expiryDate) return { label: "Date not set", tone: "warning" };
  const days = daysUntil(row.expiryDate);
  if (days === null) return { label: "Date not set", tone: "warning" };
  if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, tone: "danger" };
  if (days === 0) return { label: "Expires today", tone: "danger" };
  if (days <= EXPIRING_SOON_DAYS) return { label: `${days}d left`, tone: "warning" };
  return { label: "Valid", tone: "success" };
}

const TONE = {
  success: { fg: C.success, bg: C.successSoft, br: C.successBorder },
  warning: { fg: C.warning, bg: C.warningSoft, br: C.warningBorder },
  danger: { fg: C.danger, bg: C.dangerSoft, br: C.dangerBorder },
  info: { fg: C.info, bg: C.infoSoft, br: C.infoBorder },
  neutral: { fg: C.neutral, bg: C.neutralSoft, br: C.neutralBorder },
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLE PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────
const inputStyle = {
  width: "100%",
  minWidth: 0,
  padding: "9px 11px",
  borderRadius: R.sm,
  border: `1px solid ${C.borderStrong}`,
  fontSize: 13,
  fontFamily: F.body,
  color: C.ink,
  background: C.surface,
  boxSizing: "border-box",
};

function btn(variant = "primary") {
  const map = {
    primary: { background: C.primary, color: C.inkOnDark },
    accent: { background: C.accent, color: C.inkOnAccent },
    neutral: { background: C.neutral, color: "#fff" },
    danger: { background: C.danger, color: "#fff" },
    ghost: { background: C.surfaceAlt, color: C.ink, border: `1px solid ${C.border}` },
  };
  return {
    ...map[variant],
    border: map[variant].border || "none",
    padding: "9px 14px",
    borderRadius: R.sm,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: F.body,
    cursor: "pointer",
  };
}

function Eyebrow({ children }) {
  return (
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        color: C.accent,
        marginBottom: 4,
      }}
    >
      {children}
    </div>
  );
}

function Card({ eyebrow, title, sub, action, children }) {
  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: R.lg,
        padding: 20,
        marginBottom: 18,
        boxShadow: SH.sm,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.ink, fontFamily: F.display }}>
            {title}
          </h3>
          {sub && <div style={{ fontSize: 12.5, color: C.inkMuted, marginTop: 4 }}>{sub}</div>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, helper, children }) {
  return (
    <div style={{ marginBottom: 14, minWidth: 0 }}>
      <div style={{ fontWeight: 600, fontSize: 12.5, color: C.ink, marginBottom: 6 }}>{label}</div>
      {children}
      {helper && <div style={{ fontSize: 11.5, color: C.inkSubtle, marginTop: 5 }}>{helper}</div>}
    </div>
  );
}

function Badge({ tone = "neutral", children }) {
  const t = TONE[tone] || TONE.neutral;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: R.full,
        fontSize: 11,
        fontWeight: 700,
        color: t.fg,
        background: t.bg,
        border: `1px solid ${t.br}`,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.fg }} />
      {children}
    </span>
  );
}

function KpiTile({ label, value, tone = "neutral" }) {
  const t = TONE[tone] || TONE.neutral;
  return (
    <div
      style={{
        flex: "1 1 120px",
        minWidth: 0,
        background: t.bg,
        border: `1px solid ${t.br}`,
        borderRadius: R.md,
        padding: "12px 14px",
      }}
    >
      <div style={{ fontSize: 26, fontWeight: 800, color: t.fg, lineHeight: 1.1, fontFamily: F.display }}>
        {value}
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.inkMuted, marginTop: 3, letterSpacing: "0.02em" }}>
        {label}
      </div>
    </div>
  );
}

function Meter({ pct }) {
  const p = clampProgress(pct);
  const tone = p >= 100 ? C.success : p >= 75 ? C.primary : p >= 40 ? C.warning : C.danger;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div
        style={{
          flex: 1,
          height: 14,
          background: C.surfaceSunken,
          borderRadius: R.full,
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        <div style={{ width: `${p}%`, height: "100%", background: tone, borderRadius: R.full, transition: "width .3s" }} />
      </div>
      <div style={{ fontWeight: 700, fontSize: 13, color: C.ink, minWidth: 52, textAlign: "right" }}>
        {pct.toFixed(1)}%
      </div>
    </div>
  );
}

const tableWrap = { overflowX: "auto", marginTop: 14 };
const thStyle = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: C.inkMuted,
  borderBottom: `2px solid ${C.border}`,
  whiteSpace: "nowrap",
};
const tdStyle = { padding: "11px 12px", fontSize: 13, color: C.ink, borderBottom: `1px solid ${C.border}` };

// Expiry mode + date control reused by documents and permits
function ExpiryControl({ value, onChange }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ display: "inline-flex", border: `1px solid ${C.borderStrong}`, borderRadius: R.sm, overflow: "hidden" }}>
        {[
          { key: false, label: "Expires on date" },
          { key: true, label: "Never expires" },
        ].map((opt) => {
          const active = value.neverExpires === opt.key;
          return (
            <button
              key={String(opt.key)}
              type="button"
              onClick={() => onChange({ ...value, neverExpires: opt.key })}
              style={{
                border: "none",
                borderRadius: 0,
                padding: "8px 13px",
                fontSize: 12.5,
                fontWeight: 600,
                cursor: "pointer",
                background: active ? C.primary : C.surface,
                color: active ? C.inkOnDark : C.inkMuted,
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {!value.neverExpires && (
        <input
          type="date"
          value={value.expiryDate}
          onChange={(e) => onChange({ ...value, expiryDate: e.target.value })}
          style={{ ...inputStyle, width: "auto" }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function Contractsection({ currentProjectId, scheduledWorksTarget = 0 }) {
  const [overview, setOverview] = useState(null);
  const [overviewDraft, setOverviewDraft] = useState({});
  const [editingOverview, setEditingOverview] = useState(false);

  const [teamForm, setTeamForm] = useState(emptyTeamMember);
  const [teamMembers, setTeamMembers] = useState([]);
  const [editingTeamId, setEditingTeamId] = useState(null);

  const [documentForm, setDocumentForm] = useState(emptyDocument);
  const [documents, setDocuments] = useState([]);
  const [editingDocumentId, setEditingDocumentId] = useState(null);

  const [permitForm, setPermitForm] = useState(emptyDocument);
  const [permits, setPermits] = useState([]);
  const [editingPermitId, setEditingPermitId] = useState(null);

  const [subForm, setSubForm] = useState(emptySub);
  const [subs, setSubs] = useState([]);
  const [editingSubId, setEditingSubId] = useState(null);

  const [participationForm, setParticipationForm] = useState(emptyParticipationForm);
  const [participationEntries, setParticipationEntries] = useState([]);

  // ─── Firestore subscriptions ───────────────────────────────────────────────
  useEffect(() => {
    if (!currentProjectId) {
      setOverview(null);
      setTeamMembers([]);
      setDocuments([]);
      setPermits([]);
      setSubs([]);
      setParticipationForm(emptyParticipationForm);
      setParticipationEntries([]);
      return;
    }

    const unsubOverview = onSnapshot(
      doc(db, "projects", currentProjectId, "contractualRecords", "overview"),
      (snap) => setOverview(snap.exists() ? snap.data() : {}),
      (e) => console.error("Error loading contract overview", e)
    );

    const unsubTeam = onSnapshot(
      collection(db, "projects", currentProjectId, "team"),
      (snap) => setTeamMembers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("Error loading team members", e)
    );

    const unsubDocs = onSnapshot(
      collection(db, "projects", currentProjectId, "documents"),
      (snap) => setDocuments(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("Error loading documents", e)
    );

    const unsubPermits = onSnapshot(
      collection(db, "projects", currentProjectId, "permits"),
      (snap) => setPermits(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("Error loading permits", e)
    );

    const unsubSubs = onSnapshot(
      collection(db, "projects", currentProjectId, "subcontractors"),
      (snap) => setSubs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("Error loading subcontractors", e)
    );

    const unsubParticipation = onSnapshot(
      doc(db, "projects", currentProjectId, "contractualRecords", "participationGoals"),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setParticipationForm({
            ...emptyParticipationForm,
            cpglPercentage: data.cpglPercentage ?? "",
            cpgePercentage: data.cpgePercentage ?? "",
          });
          setParticipationEntries(data.entries || []);
        } else {
          setParticipationForm(emptyParticipationForm);
          setParticipationEntries([]);
        }
      },
      (e) => console.error("Error loading participation goals", e)
    );

    return () => {
      unsubOverview();
      unsubTeam();
      unsubDocs();
      unsubPermits();
      unsubSubs();
      unsubParticipation();
    };
  }, [currentProjectId]);

  // ─── Derived ────────────────────────────────────────────────────────────────
  const sortedDocuments = useMemo(() => {
    return [...documents].sort((a, b) => {
      const da = a.neverExpires ? Infinity : daysUntil(a.expiryDate) ?? Infinity;
      const dbb = b.neverExpires ? Infinity : daysUntil(b.expiryDate) ?? Infinity;
      return da - dbb;
    });
  }, [documents]);

  const sortedPermits = useMemo(() => {
    return [...permits].sort((a, b) => {
      const da = a.neverExpires ? Infinity : daysUntil(a.expiryDate) ?? Infinity;
      const dbb = b.neverExpires ? Infinity : daysUntil(b.expiryDate) ?? Infinity;
      return da - dbb;
    });
  }, [permits]);

  const docStats = useMemo(() => {
    const all = [...documents, ...permits];
    let expired = 0;
    let soon = 0;
    all.forEach((row) => {
      const s = expiryStatus(row);
      if (s.tone === "danger") expired += 1;
      if (s.tone === "warning" && s.label.includes("left")) soon += 1;
    });
    return { total: all.length, expired, soon };
  }, [documents, permits]);

  const scheduledWorksValue = useMemo(() => numberOrZero(scheduledWorksTarget), [scheduledWorksTarget]);
  const cpglTargetValue = useMemo(
    () => scheduledWorksValue * (numberOrZero(participationForm.cpglPercentage) / 100),
    [scheduledWorksValue, participationForm.cpglPercentage]
  );
  const cpgeTargetValue = useMemo(
    () => scheduledWorksValue * (numberOrZero(participationForm.cpgePercentage) / 100),
    [scheduledWorksValue, participationForm.cpgePercentage]
  );
  const cpglProgress = useMemo(
    () => (cpglTargetValue ? (numberOrZero(participationForm.latestCpglValue) / cpglTargetValue) * 100 : 0),
    [participationForm.latestCpglValue, cpglTargetValue]
  );
  const cpgeProgress = useMemo(
    () => (cpgeTargetValue ? (numberOrZero(participationForm.latestCpgeValue) / cpgeTargetValue) * 100 : 0),
    [participationForm.latestCpgeValue, cpgeTargetValue]
  );
  const sortedParticipationEntries = useMemo(
    () => [...participationEntries].sort((a, b) => (a.captureMonth || "").localeCompare(b.captureMonth || "")),
    [participationEntries]
  );

  // ─── Overview handlers ───────────────────────────────────────────────────────
  const startEditOverview = () => {
    setOverviewDraft(overview || {});
    setEditingOverview(true);
  };
  const saveOverview = async () => {
    if (!currentProjectId) return;
    try {
      await setDoc(doc(db, "projects", currentProjectId, "contractualRecords", "overview"), overviewDraft);
      setEditingOverview(false);
    } catch (e) {
      console.error("Error saving contract overview", e);
    }
  };

  // ─── Team handlers ────────────────────────────────────────────────────────────
  const handleSaveTeamMember = async () => {
    if (!teamForm.name || !currentProjectId) return;
    const id = editingTeamId || `TEAM-${Date.now()}`;
    try {
      await setDoc(doc(db, "projects", currentProjectId, "team", id), { ...teamForm, id });
      setTeamForm(emptyTeamMember);
      setEditingTeamId(null);
    } catch (e) {
      console.error("Error saving team member", e);
    }
  };
  const handleEditTeamMember = (row) => {
    setTeamForm({
      role: row.role || "Employer",
      name: row.name || "",
      email: row.email || "",
      organisation: row.organisation || "",
    });
    setEditingTeamId(row.id);
  };
  const handleDeleteTeamMember = async (id) => {
    if (!currentProjectId) return;
    try {
      await deleteDoc(doc(db, "projects", currentProjectId, "team", id));
      if (editingTeamId === id) {
        setTeamForm(emptyTeamMember);
        setEditingTeamId(null);
      }
    } catch (e) {
      console.error("Error deleting team member", e);
    }
  };

  // ─── Document handlers ────────────────────────────────────────────────────────
  const handleSaveDocument = async () => {
    if (!documentForm.name || !currentProjectId) return;
    const id = editingDocumentId || `DOC-${Date.now()}`;
    const payload = {
      ...documentForm,
      expiryDate: documentForm.neverExpires ? "" : documentForm.expiryDate,
      id,
    };
    try {
      await setDoc(doc(db, "projects", currentProjectId, "documents", id), payload);
      setDocumentForm(emptyDocument);
      setEditingDocumentId(null);
    } catch (e) {
      console.error("Error saving document", e);
    }
  };
  const handleEditDocument = (row) => {
    setDocumentForm({
      name: row.name || "",
      category: row.category || "Other",
      neverExpires: !!row.neverExpires,
      expiryDate: row.expiryDate || "",
      reference: row.reference || "",
    });
    setEditingDocumentId(row.id);
  };
  const handleDeleteDocument = async (id) => {
    if (!currentProjectId) return;
    try {
      await deleteDoc(doc(db, "projects", currentProjectId, "documents", id));
      if (editingDocumentId === id) {
        setDocumentForm(emptyDocument);
        setEditingDocumentId(null);
      }
    } catch (e) {
      console.error("Error deleting document", e);
    }
  };
  const quickAddDoc = (suggestion) => {
    setEditingDocumentId(null);
    setDocumentForm({ ...emptyDocument, name: suggestion.name, category: suggestion.category });
  };

  // ─── Permit handlers ──────────────────────────────────────────────────────────
  const handleSavePermit = async () => {
    if (!permitForm.name || !currentProjectId) return;
    const id = editingPermitId || `PER-${Date.now()}`;
    const payload = {
      ...permitForm,
      expiryDate: permitForm.neverExpires ? "" : permitForm.expiryDate,
      id,
    };
    try {
      await setDoc(doc(db, "projects", currentProjectId, "permits", id), payload);
      setPermitForm(emptyDocument);
      setEditingPermitId(null);
    } catch (e) {
      console.error("Error saving permit", e);
    }
  };
  const handleEditPermit = (row) => {
    setPermitForm({
      name: row.name || "",
      category: row.category || "Other",
      neverExpires: !!row.neverExpires,
      expiryDate: row.expiryDate || "",
      reference: row.reference || "",
    });
    setEditingPermitId(row.id);
  };
  const handleDeletePermit = async (id) => {
    if (!currentProjectId) return;
    try {
      await deleteDoc(doc(db, "projects", currentProjectId, "permits", id));
      if (editingPermitId === id) {
        setPermitForm(emptyDocument);
        setEditingPermitId(null);
      }
    } catch (e) {
      console.error("Error deleting permit", e);
    }
  };

  // ─── Subcontractor handlers ─────────────────────────────────────────────────
  const handleSaveSub = async () => {
    if (!subForm.name || !currentProjectId) return;
    const id = editingSubId || `SUB-${Date.now()}`;
    try {
      await setDoc(doc(db, "projects", currentProjectId, "subcontractors", id), { ...subForm, id });
      setSubForm(emptySub);
      setEditingSubId(null);
    } catch (e) {
      console.error("Error saving subcontractor", e);
    }
  };
  const handleEditSub = (row) => {
    setSubForm({
      name: row.name || "",
      type: row.type || "Subcontractor",
      scope: row.scope || "",
      performance: row.performance || "Satisfactory",
    });
    setEditingSubId(row.id);
  };
  const handleDeleteSub = async (id) => {
    if (!currentProjectId) return;
    try {
      await deleteDoc(doc(db, "projects", currentProjectId, "subcontractors", id));
      if (editingSubId === id) {
        setSubForm(emptySub);
        setEditingSubId(null);
      }
    } catch (e) {
      console.error("Error deleting subcontractor", e);
    }
  };

  // ─── Participation handler ────────────────────────────────────────────────────
  const saveParticipationGoals = async () => {
    if (!currentProjectId) return;
    const nextEntries = participationForm.captureMonth
      ? [
          ...participationEntries.filter((i) => i.captureMonth !== participationForm.captureMonth),
          {
            captureMonth: participationForm.captureMonth,
            cpglValue: numberOrZero(participationForm.latestCpglValue),
            cpgeValue: numberOrZero(participationForm.latestCpgeValue),
          },
        ]
      : participationEntries;
    try {
      await setDoc(doc(db, "projects", currentProjectId, "contractualRecords", "participationGoals"), {
        cpglPercentage: participationForm.cpglPercentage,
        cpgePercentage: participationForm.cpgePercentage,
        entries: nextEntries,
      });
      setParticipationForm((prev) => ({ ...prev, captureMonth: "", latestCpglValue: "", latestCpgeValue: "" }));
    } catch (e) {
      console.error("Error saving participation goals", e);
    }
  };

  const ov = editingOverview ? overviewDraft : overview || {};
  const setOv = (patch) => setOverviewDraft((prev) => ({ ...prev, ...patch }));

  const OVERVIEW_FIELDS = [
    { key: "contractNo", label: "Contract No." },
    { key: "description", label: "Contract description", full: true },
    { key: "employer", label: "Employer" },
    { key: "consultant", label: "Consultant / Employer's Agent" },
    { key: "contractor", label: "Contractor" },
    { key: "contractAmount", label: "Contract amount" },
    { key: "commencementDate", label: "Commencement date", type: "date" },
    { key: "completionDate", label: "Completion date", type: "date" },
    { key: "eotGranted", label: "Time extension granted" },
    { key: "revisedCompletion", label: "Revised completion date", type: "date" },
  ];

  return (
    <section style={{ fontFamily: F.body, color: C.ink }}>
      <div style={{ marginBottom: 18 }}>
        <Eyebrow>Site &amp; Admin</Eyebrow>
        <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, fontFamily: F.display, letterSpacing: "-0.02em" }}>
          Contractual Records
        </h2>
        <div style={{ fontSize: 13, color: C.inkMuted, marginTop: 4 }}>
          The contract backbone — parties, compliance, expiries and participation goals tracked across the project.
        </div>
      </div>

      {!currentProjectId && (
        <Card title="No project selected" sub="Select a project to view and capture its contractual records.">
          <div />
        </Card>
      )}

      {currentProjectId && (
        <>
          {/* ─── CONTRACT OVERVIEW ─────────────────────────────────────────── */}
          <Card
            eyebrow="Front page"
            title="Contract Overview"
            sub="Key contract data — feeds the header of every monthly write-up."
            action={
              editingOverview ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={btn("primary")} onClick={saveOverview}>Save</button>
                  <button style={btn("ghost")} onClick={() => setEditingOverview(false)}>Cancel</button>
                </div>
              ) : (
                <button style={btn("ghost")} onClick={startEditOverview}>Edit</button>
              )
            }
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
              {OVERVIEW_FIELDS.map((f) => (
                <div key={f.key} style={{ minWidth: 0, gridColumn: f.full ? "1 / -1" : "auto" }}>
                  {editingOverview ? (
                    <Field label={f.label}>
                      <input
                        type={f.type || "text"}
                        value={ov[f.key] || ""}
                        onChange={(e) => setOv({ [f.key]: e.target.value })}
                        style={inputStyle}
                      />
                    </Field>
                  ) : (
                    <div>
                      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.inkSubtle }}>
                        {f.label}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginTop: 3 }}>
                        {ov[f.key] || <span style={{ color: C.inkSubtle, fontWeight: 400 }}>—</span>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* ─── COMPLIANCE & DOCUMENT REGISTER ─────────────────────────────── */}
          <Card
            eyebrow="Clause 5.3.1 · pre-commencement & ongoing"
            title="Compliance & Document Register"
            sub="Statutory letters, insurances and certificates. Set an expiry date or mark as never expiring."
          >
            {/* KPI tiles */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
              <KpiTile label="Items tracked" value={docStats.total} tone="info" />
              <KpiTile label="Expiring ≤30 days" value={docStats.soon} tone="warning" />
              <KpiTile label="Expired" value={docStats.expired} tone="danger" />
            </div>

            {/* Quick add */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: C.inkMuted, marginBottom: 8 }}>
                Quick add a standard document:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
                {SUGGESTED_DOCS.map((s) => {
                  const exists = documents.some((d) => d.name === s.name);
                  return (
                    <button
                      key={s.name}
                      type="button"
                      disabled={exists}
                      onClick={() => quickAddDoc(s)}
                      style={{
                        padding: "5px 11px",
                        borderRadius: R.full,
                        border: `1px solid ${exists ? C.border : C.primaryAccent}`,
                        background: exists ? C.surfaceAlt : C.primarySoft,
                        color: exists ? C.inkSubtle : C.primary,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: exists ? "default" : "pointer",
                      }}
                    >
                      {exists ? "✓ " : "+ "}
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Form */}
            <div style={{ background: C.surfaceAlt, borderRadius: R.md, padding: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
                <Field label="Document name">
                  <input
                    type="text"
                    placeholder="e.g. Letter of Good Standing — COID"
                    value={documentForm.name}
                    onChange={(e) => setDocumentForm({ ...documentForm, name: e.target.value })}
                    style={inputStyle}
                  />
                </Field>
                <Field label="Category">
                  <select
                    value={documentForm.category}
                    onChange={(e) => setDocumentForm({ ...documentForm, category: e.target.value })}
                    style={inputStyle}
                  >
                    {DOC_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Validity" helper="Choose “Never expires” for one-off declarations; otherwise capture the current expiry date.">
                <ExpiryControl value={documentForm} onChange={setDocumentForm} />
              </Field>
              <Field label="Reference / notes">
                <input
                  type="text"
                  placeholder="Optional — e.g. policy no, issuing body"
                  value={documentForm.reference}
                  onChange={(e) => setDocumentForm({ ...documentForm, reference: e.target.value })}
                  style={inputStyle}
                />
              </Field>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={btn("primary")} onClick={handleSaveDocument}>
                  {editingDocumentId ? "Update document" : "Add document"}
                </button>
                {editingDocumentId && (
                  <button style={btn("ghost")} onClick={() => { setDocumentForm(emptyDocument); setEditingDocumentId(null); }}>
                    Cancel
                  </button>
                )}
              </div>
            </div>

            {/* Table */}
            <div style={tableWrap}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Document</th>
                    <th style={thStyle}>Category</th>
                    <th style={thStyle}>Expiry</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Reference</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDocuments.length === 0 && (
                    <tr><td style={{ ...tdStyle, color: C.inkSubtle }} colSpan={6}>No documents captured yet.</td></tr>
                  )}
                  {sortedDocuments.map((row) => {
                    const s = expiryStatus(row);
                    return (
                      <tr key={row.id}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{row.name}</td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: 11.5, color: C.inkMuted }}>{row.category || "—"}</span>
                        </td>
                        <td style={tdStyle}>{row.neverExpires ? "—" : row.expiryDate || "—"}</td>
                        <td style={tdStyle}><Badge tone={s.tone}>{s.label}</Badge></td>
                        <td style={{ ...tdStyle, color: C.inkMuted, fontSize: 12 }}>{row.reference || "—"}</td>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button style={{ ...btn("ghost"), padding: "6px 10px", fontSize: 12 }} onClick={() => handleEditDocument(row)}>Edit</button>
                            <button style={{ ...btn("danger"), padding: "6px 10px", fontSize: 12 }} onClick={() => handleDeleteDocument(row.id)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ─── WAYLEAVES & PERMITS ────────────────────────────────────────── */}
          <Card
            eyebrow="Authorisations"
            title="Wayleaves & Permits"
            sub="Road opening, electrical wayleaves, traffic accommodation and similar approvals."
          >
            <div style={{ background: C.surfaceAlt, borderRadius: R.md, padding: 16 }}>
              <Field label="Permit / wayleave name">
                <input
                  type="text"
                  placeholder="e.g. Road Opening Permit, Electrical Wayleave"
                  value={permitForm.name}
                  onChange={(e) => setPermitForm({ ...permitForm, name: e.target.value })}
                  style={inputStyle}
                />
              </Field>
              <Field label="Validity">
                <ExpiryControl value={permitForm} onChange={setPermitForm} />
              </Field>
              <Field label="Reference / notes">
                <input
                  type="text"
                  placeholder="Optional — e.g. permit no, issuing authority"
                  value={permitForm.reference}
                  onChange={(e) => setPermitForm({ ...permitForm, reference: e.target.value })}
                  style={inputStyle}
                />
              </Field>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={btn("primary")} onClick={handleSavePermit}>
                  {editingPermitId ? "Update permit" : "Add permit / wayleave"}
                </button>
                {editingPermitId && (
                  <button style={btn("ghost")} onClick={() => { setPermitForm(emptyDocument); setEditingPermitId(null); }}>
                    Cancel
                  </button>
                )}
              </div>
            </div>

            <div style={tableWrap}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Permit / Wayleave</th>
                    <th style={thStyle}>Expiry</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Reference</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPermits.length === 0 && (
                    <tr><td style={{ ...tdStyle, color: C.inkSubtle }} colSpan={5}>No permits captured yet.</td></tr>
                  )}
                  {sortedPermits.map((row) => {
                    const s = expiryStatus(row);
                    return (
                      <tr key={row.id}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{row.name}</td>
                        <td style={tdStyle}>{row.neverExpires ? "—" : row.expiryDate || "—"}</td>
                        <td style={tdStyle}><Badge tone={s.tone}>{s.label}</Badge></td>
                        <td style={{ ...tdStyle, color: C.inkMuted, fontSize: 12 }}>{row.reference || "—"}</td>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button style={{ ...btn("ghost"), padding: "6px 10px", fontSize: 12 }} onClick={() => handleEditPermit(row)}>Edit</button>
                            <button style={{ ...btn("danger"), padding: "6px 10px", fontSize: 12 }} onClick={() => handleDeletePermit(row.id)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ─── CONTRACT PARTICIPATION GOALS ───────────────────────────────── */}
          <Card
            eyebrow="Transformation"
            title="Contract Participation Goals"
            sub="CPGL and CPGE targets against the scheduled works value, tracked monthly."
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginBottom: 14 }}>
              <Field label="Target value (scheduled works)">
                <input type="text" value={scheduledWorksValue.toLocaleString()} readOnly style={{ ...inputStyle, background: C.surfaceSunken, fontWeight: 700 }} />
              </Field>
              <Field label="CPGL %" helper={`Target: ${cpglTargetValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}>
                <input
                  type="number"
                  placeholder="CPGL %"
                  value={participationForm.cpglPercentage}
                  onChange={(e) => setParticipationForm({ ...participationForm, cpglPercentage: e.target.value })}
                  style={inputStyle}
                />
              </Field>
              <Field label="CPGE %" helper={`Target: ${cpgeTargetValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}>
                <input
                  type="number"
                  placeholder="CPGE %"
                  value={participationForm.cpgePercentage}
                  onChange={(e) => setParticipationForm({ ...participationForm, cpgePercentage: e.target.value })}
                  style={inputStyle}
                />
              </Field>
            </div>

            <div style={{ background: C.surfaceAlt, borderRadius: R.md, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
                <Field label="Capture month">
                  <input
                    type="month"
                    value={participationForm.captureMonth}
                    onChange={(e) => setParticipationForm({ ...participationForm, captureMonth: e.target.value })}
                    style={inputStyle}
                  />
                </Field>
                <Field label="Latest CPGL value">
                  <input
                    type="number"
                    value={participationForm.latestCpglValue}
                    onChange={(e) => setParticipationForm({ ...participationForm, latestCpglValue: e.target.value })}
                    style={inputStyle}
                  />
                </Field>
                <Field label="Latest CPGE value">
                  <input
                    type="number"
                    value={participationForm.latestCpgeValue}
                    onChange={(e) => setParticipationForm({ ...participationForm, latestCpgeValue: e.target.value })}
                    style={inputStyle}
                  />
                </Field>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 6 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.inkMuted, marginBottom: 6 }}>CPGL progress</div>
                  <Meter pct={cpglProgress} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.inkMuted, marginBottom: 6 }}>CPGE progress</div>
                  <Meter pct={cpgeProgress} />
                </div>
              </div>
              <div style={{ marginTop: 14 }}>
                <button style={btn("primary")} onClick={saveParticipationGoals}>Save participation goals</button>
              </div>
            </div>

            <div style={tableWrap}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Month</th>
                    <th style={thStyle}>CPGL value</th>
                    <th style={thStyle}>CPGL progress</th>
                    <th style={thStyle}>CPGE value</th>
                    <th style={thStyle}>CPGE progress</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedParticipationEntries.length === 0 && (
                    <tr><td style={{ ...tdStyle, color: C.inkSubtle }} colSpan={5}>No monthly captures yet.</td></tr>
                  )}
                  {sortedParticipationEntries.map((row) => {
                    const lp = cpglTargetValue ? (numberOrZero(row.cpglValue) / cpglTargetValue) * 100 : 0;
                    const ep = cpgeTargetValue ? (numberOrZero(row.cpgeValue) / cpgeTargetValue) * 100 : 0;
                    return (
                      <tr key={row.captureMonth}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{row.captureMonth}</td>
                        <td style={tdStyle}>{numberOrZero(row.cpglValue).toLocaleString()}</td>
                        <td style={tdStyle}>{lp.toFixed(1)}%</td>
                        <td style={tdStyle}>{numberOrZero(row.cpgeValue).toLocaleString()}</td>
                        <td style={tdStyle}>{ep.toFixed(1)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ─── PROJECT TEAM & PARTIES ─────────────────────────────────────── */}
          <Card eyebrow="9.1 Attendance" title="Project Team & Parties" sub="Contractual contacts across all organisations.">
            <div style={{ background: C.surfaceAlt, borderRadius: R.md, padding: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
                <Field label="Role">
                  <select value={teamForm.role} onChange={(e) => setTeamForm({ ...teamForm, role: e.target.value })} style={inputStyle}>
                    <option value="Employer">Employer</option>
                    <option value="Employer's Agent">Employer's Agent</option>
                    <option value="Engineer">Engineer</option>
                    <option value="Engineer's Assistant">Engineer's Assistant</option>
                    <option value="Contracts Manager">Contracts Manager</option>
                    <option value="OHS Agent">OHS Agent</option>
                    <option value="Environmental">Environmental</option>
                    <option value="Contractor">Contractor</option>
                  </select>
                </Field>
                <Field label="Name">
                  <input type="text" value={teamForm.name} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} style={inputStyle} />
                </Field>
                <Field label="Organisation">
                  <input type="text" value={teamForm.organisation} onChange={(e) => setTeamForm({ ...teamForm, organisation: e.target.value })} style={inputStyle} />
                </Field>
                <Field label="Email">
                  <input type="email" value={teamForm.email} onChange={(e) => setTeamForm({ ...teamForm, email: e.target.value })} style={inputStyle} />
                </Field>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={btn("primary")} onClick={handleSaveTeamMember}>
                  {editingTeamId ? "Update member" : "Add member"}
                </button>
                {editingTeamId && (
                  <button style={btn("ghost")} onClick={() => { setTeamForm(emptyTeamMember); setEditingTeamId(null); }}>Cancel</button>
                )}
              </div>
            </div>

            <div style={tableWrap}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Role</th>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Organisation</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.length === 0 && (
                    <tr><td style={{ ...tdStyle, color: C.inkSubtle }} colSpan={5}>No team members captured yet.</td></tr>
                  )}
                  {teamMembers.map((row) => (
                    <tr key={row.id}>
                      <td style={tdStyle}><Badge tone="info">{row.role}</Badge></td>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{row.name}</td>
                      <td style={tdStyle}>{row.organisation || "—"}</td>
                      <td style={{ ...tdStyle, color: C.inkMuted, fontSize: 12 }}>{row.email || "—"}</td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button style={{ ...btn("ghost"), padding: "6px 10px", fontSize: 12 }} onClick={() => handleEditTeamMember(row)}>Edit</button>
                          <button style={{ ...btn("danger"), padding: "6px 10px", fontSize: 12 }} onClick={() => handleDeleteTeamMember(row.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ─── SUBCONTRACTORS & TARGETED ENTERPRISES ──────────────────────── */}
          <Card
            eyebrow="9.9 Subcontractors"
            title="Subcontractors & Targeted Enterprises"
            sub="Appointed subcontractors and targeted enterprises with performance status."
          >
            <div style={{ background: C.surfaceAlt, borderRadius: R.md, padding: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
                <Field label="Name">
                  <input type="text" value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} style={inputStyle} />
                </Field>
                <Field label="Type">
                  <select value={subForm.type} onChange={(e) => setSubForm({ ...subForm, type: e.target.value })} style={inputStyle}>
                    <option value="Subcontractor">Subcontractor</option>
                    <option value="Targeted Enterprise">Targeted Enterprise</option>
                  </select>
                </Field>
                <Field label="Scope / description">
                  <input type="text" value={subForm.scope} onChange={(e) => setSubForm({ ...subForm, scope: e.target.value })} style={inputStyle} />
                </Field>
                <Field label="Performance">
                  <select value={subForm.performance} onChange={(e) => setSubForm({ ...subForm, performance: e.target.value })} style={inputStyle}>
                    <option value="Good">Good</option>
                    <option value="Satisfactory">Satisfactory</option>
                    <option value="Poor">Poor</option>
                    <option value="Not yet rated">Not yet rated</option>
                  </select>
                </Field>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={btn("primary")} onClick={handleSaveSub}>
                  {editingSubId ? "Update entry" : "Add entry"}
                </button>
                {editingSubId && (
                  <button style={btn("ghost")} onClick={() => { setSubForm(emptySub); setEditingSubId(null); }}>Cancel</button>
                )}
              </div>
            </div>

            <div style={tableWrap}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Scope</th>
                    <th style={thStyle}>Performance</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.length === 0 && (
                    <tr><td style={{ ...tdStyle, color: C.inkSubtle }} colSpan={5}>No subcontractors captured yet.</td></tr>
                  )}
                  {subs.map((row) => {
                    const perfTone =
                      row.performance === "Good" ? "success" :
                      row.performance === "Poor" ? "danger" :
                      row.performance === "Satisfactory" ? "info" : "neutral";
                    return (
                      <tr key={row.id}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{row.name}</td>
                        <td style={tdStyle}>
                          <Badge tone={row.type === "Targeted Enterprise" ? "warning" : "neutral"}>{row.type}</Badge>
                        </td>
                        <td style={tdStyle}>{row.scope || "—"}</td>
                        <td style={tdStyle}><Badge tone={perfTone}>{row.performance}</Badge></td>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button style={{ ...btn("ghost"), padding: "6px 10px", fontSize: 12 }} onClick={() => handleEditSub(row)}>Edit</button>
                            <button style={{ ...btn("danger"), padding: "6px 10px", fontSize: 12 }} onClick={() => handleDeleteSub(row.id)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </section>
  );
}